
import { ValidatorFn, AbstractControl } from '@angular/forms';
import { _ } from '@icc/shared/helpers';
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { PageComponent, ICC_PAGE_DATA, ModalService } from '@icc/configurator/shared';
import { SharedFacade } from '@icc/configurator/shared';
import { IccAccessoryAccessory, IccAccessoryColor } from '@icc/construction-elements/types';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ConfigurationsService } from '@icc/configurator/shared';
import { TranslateService, APP_CONFIG, AppConfigFactory } from '@icc/shared';
import { EventBusService } from '@icc/shared';
import { ActiveSash } from '@icc/window-door/types';
import { BehaviorSubject, Subscription } from 'rxjs';
import { WindowSillsService } from '../window-sills.service';
import { UnitConverterService } from '@icc/configurator/ui';

@Component({
    selector: 'icc-sill-options-page',
    templateUrl: './sill-options-page.component.html',
    styleUrls: ['./sill-options-page.component.scss'],
    standalone: false
})
export class SillOptionsPageComponent extends PageComponent implements OnInit, OnDestroy {
    public title: string = _('ACCESSORY|Opcje dodatku');

    accessory: any;
    form: UntypedFormGroup;
    focused = false;
    showCoreColorInAdditionalProfilesAndAccessories = this.config().IccConfig.Configurators
        .showCoreColorInAdditionalProfilesAndAccessories;
    currency: any = null;
    currentPrice$ = new BehaviorSubject<string | number>('');
    private subscriptions: Subscription[] = [];

    dimensionUnit = this.unitConverterService.getUnit();

    constructor(
        private translateService: TranslateService,
        private configurationsService: ConfigurationsService<'window'>,
        private sharedFacade: SharedFacade,
        private modalService: ModalService,
        private windowSillsService: WindowSillsService,
        private fb: UntypedFormBuilder,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        @Inject(ICC_PAGE_DATA)
        private pageData: {
            accessory: IccAccessoryAccessory;
            forIt: 'configuration' | 'sash' | 'side';
            sash?: ActiveSash;
            colors: IccAccessoryColor[];
        },
        private unitConverterService: UnitConverterService
    ) {
        super();
    }

    ngOnInit() {
        this.accessory = this.pageData.accessory;
        this.form = this.fb.group({
            count: [this.accessory.count || 1, Validators.min(1)],
            selectedColor: [this.accessory.selectedColor || '0', Validators.required],
            plugs: 0,
            currentWidth: [
                0,
                [Validators.min(this.accessory.min_width),
                Validators.max(this.accessory.max_width),
                requiredWhenValidator(this.accessory.min_width, this.accessory.max_width)],
            ],
            currentLength: [
                0,
                [Validators.min(this.accessory.min_length),
                Validators.max(this.accessory.max_length),
                requiredWhenValidator(this.accessory.min_length, this.accessory.max_length)],
            ],
            in_range: false,
            comment: '',
        });

        if (this.accessory.blockCountChange) {
            this.form.get('count').disable();
        }

        this.subscriptions.push(
            this.sharedFacade.currency$.subscribe(currency => {
                this.currency = currency;
            })
        );
        this.subscriptions.push(
            this.form.valueChanges.subscribe(value => {
                this.currentPrice$.next(this.getPrice());
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    add() {
        if (this.form.valid) {
            Object.assign(this.accessory, this.form.value);
            if (!this.accessory.no_price) {
                this.windowSillsService.calcSill(this.accessory);
            }
            this.sharedFacade.closePage({
                accessory: this.accessory,
            });
        }
    }

    canAdd() {
        return true;
    }

    getPrice() {
        if (!this.accessory.no_price) {
            return this.windowSillsService.calcSill({ ...this.accessory, ...this.form.value });
        }
        return this.translateService.instant('CONFIGURATOR|Do wyceny');
    }

    getAccessoryColors(accessory: IccAccessoryAccessory) {
        return (this.pageData.colors || []).filter(
            c => accessory.colors_ids && accessory.colors_ids.map(Number).includes(Number(c.id))
        );
    }

    getSillWidths(sill) {
        return this.windowSillsService.getSillWidths(sill);
    }

    getSillLengths(sill) {
        return this.windowSillsService.getSillLengths(sill);
    }

    setInRange(sill) {
        this.windowSillsService.setInRange(sill);
    }
}

function requiredWhenValidator(minValue: string | number, maxValue: string | number): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
        const requiredWidth = ((+minValue > 0) || (+maxValue > 0)) ? true : false
        return !(control.value) && requiredWidth ? {'required': true} : null
    };
}
